import {
  Component,
  Input,
  OnInit,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SelectItem, PrimeTemplate } from '@openng/optimus-ui/api';
import { BaseComponent } from 'src/app/core/components/base.component';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { Availability } from 'src/app/model/contacts/availability';
import { Contact } from 'src/app/model/contacts/contact';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { Panel } from '@openng/optimus-ui/panel';
import { InputText } from '@openng/optimus-ui/inputtext';
import { Select } from '@openng/optimus-ui/select';
import { AutoComplete } from '@openng/optimus-ui/autocomplete';
import { ButtonDirective, Button } from '@openng/optimus-ui/button';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { TableModule } from '@openng/optimus-ui/table';
import { InputMask } from '@openng/optimus-ui/inputmask';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

export type PhoneFieldName = 'bm' | 'bp' | 'pm' | 'home' | 'other';

@Component({
  selector: 'app-contact-information-form',
  templateUrl: './contact-information-form.component.html',
  styleUrls: ['./contact-information-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Panel,
    InputText,
    Select,
    AutoComplete,
    ButtonDirective,
    DatePicker,
    ScrollPanel,
    TableModule,
    PrimeTemplate,
    Button,
    InputMask,
    Tooltip,
    DatePipe,
    TranslatePipe,
  ],
})
export class ContactInformationFormComponent
  extends BaseComponent
  implements OnInit
{
  private _logAndMessage = inject(LogAndMessageService);
  private _deviceDetector = inject(DeviceDetectorService);

  DirtyStatus = DirtyStatus;
  readonly contact = input<Contact>();
  readonly form = input<UntypedFormGroup>();
  readonly availableCompanies = input<any[]>([]);
  readonly availableJobRoles = input<any[]>([]);
  readonly modifyButtonLabel = input.required<string>();
  readonly modifyButtonIcon = input.required<string>();
  readonly selectedAvailability = input<Availability | null>();
  readonly showWhenUnavailable = input<boolean>();
  readonly canViewPII = input<boolean>();
  private lStartTime!: Date | null;
  @Input() set startTime(date: Date | null) {
    this.lStartTime = date;
    if (date) {
      this.disableStart = date < this.today;
    } else {
      this.disableStart = false;
    }
  }

  get startTime() {
    return this.lStartTime;
  }

  @Input() endTime!: Date | null;
  readonly filteredContacts = input<SelectItem[]>([]);
  readonly availableContactMethods = input<SelectItem[]>([]);
  readonly writeAccess = input(false);
  readonly goToContactEvent = output<any>();
  readonly modifyAvailabilityEvent = output<any>();
  readonly selectAvailabiityEvent = output<any>();
  readonly clearAvailabilityEvent = output();
  readonly updateAvailabilityEvent = output<any>();
  readonly deleteAvailabilityEvent = output<any>();
  readonly supervisorSearchEvent = output<any>();
  readonly selectSupervisorEvent = output<any>();
  readonly whenUnavailableSearchEvent = output<any>();
  readonly selectWhenUnavailableEvent = output<any>();
  readonly formPristineEvent = output();

  startCollapsed = false;
  minDate = new Date();
  today = new Date();
  defaultStartDate = new Date();
  defaultEndDate = new Date();
  disableStart = false;
  AvailabilityPurpose = 'Add';
  phoneMasks: Map<PhoneFieldName, string> = new Map();
  maskButtonVisible: Map<PhoneFieldName, boolean> = new Map();

  constructor() {
    super();
    this.phoneMasks.set('bp', '+1-999-999-9999');
    this.phoneMasks.set('bm', '+1-999-999-9999');
    this.phoneMasks.set('pm', '+1-999-999-9999');
    this.phoneMasks.set('home', '+1-999-999-9999');
    this.phoneMasks.set('other', '+1-999-999-9999');
    this.maskButtonVisible.set('bp', false);
    this.maskButtonVisible.set('bm', false);
    this.maskButtonVisible.set('pm', false);
    this.maskButtonVisible.set('home', false);
    this.maskButtonVisible.set('other', false);
  }

  ngOnInit() {
    if (this._deviceDetector.isMobile()) {
      this.startCollapsed = true;
    }

    let today = new Date();

    this.defaultStartDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDay(),
      15,
      30
    );
    this.defaultEndDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDay(),
      7,
      0
    );
  }

  isShown: boolean = false;

  openAvailability() {
    if (this.isShown == false) {
      this.isShown = true;
    } else if (this.isShown == true) {
      this.isShown = false;
    }
    this.AvailabilityPurpose = 'Add';
  }

  modifyAvailability() {
    if (this.validateDates()) {
      this.modifyAvailabilityEvent.emit({
        startTime: this.startTime,
        endTime: this.endTime,
        selected: this.selectedAvailability(),
      });
      this.clearAvailability();
    }
  }

  validateDates(): boolean {
    let retVal = true;

    this.form()?.updateValueAndValidity();
    if (!this.form()?.valid) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CONTACT.MESSAGES.HEADERS.UNFILLED_FORM_FIELDS',
        bodyKey: 'CONTACT.MESSAGES.WARN.UNFILLED_FORM_FIELDS',
      });
      return false;
    }

    if (!this.endTime || !this.startTime || this.endTime <= this.startTime) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CONTACT.MESSAGES.HEADERS.INVALID_DATES',
        bodyKey: 'CONTACT.MESSAGES.WARN.INVALID_DATES_MSG',
      });
      retVal = false;
    }

    this.contact()?.availabilities.forEach((a) => {
      const startD = new Date(a.start);
      const endD = new Date(a.end);
      const selectedAvailability = this.selectedAvailability();
      if (
        (!selectedAvailability ||
          (selectedAvailability && selectedAvailability.id !== a.id)) &&
        ((this.startTime! >= startD && this.startTime! <= endD) ||
          (this.endTime! <= endD && this.endTime! >= startD))
      ) {
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CONTACT.MESSAGES.HEADERS.INVALID_DATES',
          bodyKey: 'CONTACT.MESSAGES.WARN.OVERLAPPING_DATES',
        });
        retVal = false;
      }
    });

    return retVal;
  }

  clearAvailability() {
    this.startTime = null;
    this.endTime = null;
    this.disableStart = false;
    this.clearAvailabilityEvent.emit();
  }

  canBeModified(avail: Availability) {
    return new Date(avail.end) > this.today;
  }

  disabledForContact(): boolean {
    const contact = this.contact();
    return !isNullOrUndefined(contact) && !isNullOrUndefined(contact!.id);
  }

  setMask(country: 'US' | 'MX', phoneField: PhoneFieldName) {
    // Note: If these masks are ever changed in the future, make sure to update the
    // regex validator in contact-builder#buildForm to match
    if (country === 'US') {
      this.phoneMasks.set(phoneField, '+1-999-999-9999');
    } else if (country === 'MX') {
      this.phoneMasks.set(phoneField, '+52-99-9999-9999');
    }
  }

  showMaskButtons(phoneField: PhoneFieldName) {
    this.maskButtonVisible.set(phoneField, true);
  }

  setMaskBasedOnFieldValue(phoneField: PhoneFieldName, value: string) {
    if (!value) {
      // Do nothing if there's no value
      return;
    }

    if (value.includes('+52')) {
      this.setMask('MX', phoneField);
    } else {
      this.setMask('US', phoneField);
    }
  }

  setMasksBasedOnFieldValues() {
    const contact = this.contact();
    this.setMaskBasedOnFieldValue('bm', contact!.businessMobile);
    this.setMaskBasedOnFieldValue('bp', contact!.businessPhone);
    this.setMaskBasedOnFieldValue('pm', contact!.personalMobile);
    this.setMaskBasedOnFieldValue('home', contact!.home);
    this.setMaskBasedOnFieldValue('other', contact!.other);

    // Re-patch values in case they got removed when changing mask
    setTimeout(() => {
      this.form()!.patchValue({
        businessPhone: this.contact()!.businessPhone,
        businessMobile: this.contact()!.businessMobile,
        personalMobile: this.contact()!.personalMobile,
        home: this.contact()!.home,
        other: this.contact()!.other,
      });

      this.form()!.markAsPristine();
      this.formPristineEvent.emit();
    }, 50);
  }
}
